import { useState, useEffect, useCallback } from "react";
import './career.css';
import "./NewEngineering.css";
import { NavLink } from "react-router-dom";
import Footer from "../Component/NewFooter";
import SplashScreen from "../Component/SplashScreen";
import axios from "axios";

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
const CACHE_KEY      = "jeyshawn_construction_data";

const servicesNav = [
  { label: "Engineering",                    href: "/engineering" },
  { label: "Procurement",                    href: "/procurement" },
  { label: "Construction & Camp Setup",       href: "/construction" },
  { label: "Installation & Instrumentation",  href: "/installation-instrumentation" },
  { label: "Commissioning",                   href: "/commissioning" },
  { label: "Manpower-supply",                 href: "/manpower-supply" },
  { label: "Marine Support",                  href: "/marine-support" },
];

/* ── Helpers ───────────────────────────────────────────────────── */
function parseConstructionData(db) {
  return {
    title:          db?.title          || "",
    paragraphs:     Array.isArray(db?.paragraphs) ? db.paragraphs : [],
    bannerImage:    db?.bannerImage    || "",
    bgImage:        db?.bgImage        || "",
    secondaryImage: db?.secondaryImage || "",
    campSetup: {
      heading:         db?.extraSection?.heading       || "",
      intro:           db?.extraSection?.paragraphs?.[0] || "",
      outro:           db?.extraSection?.paragraphs?.[1] || "",
      servicesHeading: db?.extraSection?.bulletHeading  || "",
      serviceItems:    Array.isArray(db?.extraSection?.bulletItems) ? db.extraSection.bulletItems : [],
    },
  };
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
export default function Construction() {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/construction`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseConstructionData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/construction:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;

  const data = pageData;

  return (
    <>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carriercon">
        <img src={data.bannerImage} alt="" />
      </div>

      <section className="service-detail" style={{ backgroundImage: `url(${data.bgImage})` }}>
        <div className="service-detail__layout">
          <div className="service-detail__content">
            <h2 className="service-detail__title">{data.title}</h2>
            <span className="service-detail__divider" />
            {data.paragraphs.map((p, i) => (
              <p className="service-detail__intro" key={i}>{p}</p>
            ))}

            <p className="intro-text mkbold remove_margin" style={{ color: '#008000' }}>
              {data.campSetup.heading}
            </p>
            <p className="service-detail__intro">{data.campSetup.intro}</p>
            <p className="service-detail__intro">{data.campSetup.outro}</p>

            <p className="intro-text mkbold">{data.campSetup.servicesHeading}</p>
            <ul className="catext">
              {data.campSetup.serviceItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>

          <nav className="service-detail__tabs" aria-label="Service navigation">
            {servicesNav.map((s) => (
              <NavLink
                key={s.href}
                to={s.href}
                className={({ isActive }) =>
                  `service-detail__tab${isActive ? " service-detail__tab--active" : ""}`
                }
              >
                {s.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </section>
      <Footer />
    </>
  );
}